import { CheckoutPage } from './CheckoutPage'
import { DeliveryAreasPage } from './DeliveryAreasPage'
import { useDeliveryAreasOffered } from './use-delivery-areas'
import { PaymentErrorPage, PaymentPendingPage, SuccessPage } from './LandingPages'

/**
 * `customer-checkout` — the basket, the address, the money, and the order.
 *
 * Three routes: the checkout itself, and the two pages the payment gateway lands on afterwards. The
 * landings are here because they read the note the checkout left behind — `OnlinePaymentService.Landing`
 * is two bare paths with no query string, so nothing else can tell them which order they are about.
 *
 * It is its own module rather than part of the menu because it owns the arithmetic that
 * decides what a customer is charged, and that arithmetic is where four of this migration's findings
 * live: the coupon valued to the cent rather than the coin (**FB-C04**, **FB-C07**), the product-scoped
 * coupon pinned to its lines (**FB-C08**), the preorder instant that survives the clock change
 * (**FB-C09**), and the delivery zone drawn the way the server draws it (**FB-C10**).
 *
 * `/checkout` is legacy's path. An earlier draft renamed it `/pokladna` for consistency with the Slovak
 * routes beside it, which was **FB-C12**: a public URL is not ours to tidy.
 */
export const customerCheckoutModule = {
  name: 'customer-checkout',
  routes: [
    { path: '/checkout', page: 'Checkout', component: CheckoutPage },
    { path: '/success', page: 'Success', component: SuccessPage },
    { path: '/payment-error', page: 'PaymentError', component: PaymentErrorPage },
    // §9.793 - `Landing.PENDING`'s path, which the server has redirected to since the first port and nothing served.
    { path: '/payment-pending', page: 'PaymentPending', component: PaymentPendingPage },
    // §9.799, owner O148 - where the restaurant delivers and what it costs; AGOS-only, legacy has no such page.
    { path: '/oblasti-dorucenia', page: 'OblastiDorucenia', component: DeliveryAreasPage },
  ],
} as const

export { CheckoutPage }
export { DeliveryAreasPage, useDeliveryAreasOffered }
export { deliveryAreasOffered, priceRows, shownZones, zoneOutline, type PriceRow } from './delivery-areas'
export { PaymentErrorPage, PaymentPendingPage, SuccessPage }
export {
  couponCents,
  couponFrom,
  couponRefusal,
  referralDiscountCents,
  stillApplies,
  type AppliedCoupon,
  type CouponLine,
} from './coupon'
export {
  deliveryFor,
  deliveryNote,
  feeCentsFor,
  nextCheaperTier,
  zoneFor,
  zonesOf,
  type DeliveryZone,
  type ZoneOutcome,
} from './delivery'
export {
  PAYMENT_FALLBACK,
  paymentMethodLabel,
  paymentMethodNote,
  paymentMethodsFor,
  paymentSettingsOf,
  selectedPaymentMethod,
  type OrderType,
  type PaymentMethod,
  type PaymentSettings,
} from './payment-methods'
export { preorderProblem, scheduledAtOf } from './preorder'
export {
  lineOf,
  orderPayload,
  submitProblems,
  type CheckoutFacts,
  type OrderLinePayload,
  type PricedCartLine,
} from './order-payload'

export {
  activePromoOf,
  blackFridayCents,
  blackFridayLabel,
  freeDeliveryApplies,
  type ActivePromo,
} from './promo'
